// comprehensiveHtmlCleaner.ts - Complete HTML cleaner with special handling for text tags


/**
 * Comprehensive HTML cleaner that:
 * 1. Removes BR tags
 * 2. Converts DIVs to P tags for text content
 * 3. Fixes improper nesting of text formatting elements
 * 4. Removes empty elements and unnecessary attributes
 * 5. Performs a final pass to unwrap text tags from paragraphs
 * 
 * @param html The HTML content to clean
 * @returns Cleaned HTML
 */
export const cleanHtml = (html: string): string => {
    if (!html) return '';
    
    try {
      // STEP 1: First remove all break tags via string replacement
      let cleanedHtml = html.replace(/<br[^>]*>/gi, '');
      
      // STEP 2: Create a temporary element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanedHtml;
      
      // STEP 3: Remove all unnecessary inline styles
      const elementsWithStyle = tempDiv.querySelectorAll('[style]');
      elementsWithStyle.forEach(el => {
        el.removeAttribute('style');
      });
      
      // STEP 4: Convert DIVs to P tags when they contain only text content
      const convertDivsToParagraphs = () => {
        // Find all divs that only contain text or inline elements
        const textDivs = Array.from(tempDiv.querySelectorAll('div')).filter(div => {
          // Check if the div contains any block elements
          const hasBlockElements = div.querySelector('div, p, h1, h2, h3, h4, h5, h6, ul, ol, table, blockquote');
          return !hasBlockElements && div.textContent && div.textContent.trim() !== '';
        });
        
        // Convert text divs to paragraphs
        textDivs.forEach(div => {
          try {
            // Create a new paragraph
            const newP = document.createElement('p');
            
            // Copy all attributes
            for (let i = 0; i < div.attributes.length; i++) {
              const attr = div.attributes[i];
              newP.setAttribute(attr.name, attr.value);
            }
            
            // Move all children
            while (div.firstChild) {
              newP.appendChild(div.firstChild);
            }
            
            // Replace the div with the paragraph
            if (div.parentNode) {
              div.parentNode.replaceChild(newP, div);
            }
          } catch (err) {
            console.warn('Error converting div to paragraph:', err);
          }
        });
      };
      
      // Apply div to paragraph conversion
      try {
        convertDivsToParagraphs();
      } catch (err) {
        console.warn('Error during div conversion:', err);
      }
      
      // STEP 5: Fix spans with formatting content
      const fixSpanFormatting = () => {
        // Find all spans with only formatting content
        const formattingSpans = Array.from(tempDiv.querySelectorAll('span')).filter(span => {
          return span.childNodes.length === 1 && 
                 span.firstChild instanceof Element &&
                 ['B', 'STRONG', 'I', 'EM', 'U'].includes(span.firstChild.tagName);
        });
        
        // Replace spans with their formatting content
        formattingSpans.forEach(span => {
          try {
            const formattingEl = span.firstChild as Element;
            
            if (span.parentNode) {
              span.parentNode.replaceChild(formattingEl, span);
            }
          } catch (err) {
            console.warn('Error fixing formatting span:', err);
          }
        });
        
        // Find spans with only text and convert to paragraphs
        const textSpans = Array.from(tempDiv.querySelectorAll('span')).filter(span => {
          // Check if it only contains text (no block elements)
          const hasBlockElements = span.querySelector('div, p, h1, h2, h3, h4, h5, h6, ul, ol, table');
          return !hasBlockElements && span.textContent && span.textContent.trim() !== '';
        });
        
        // Convert these spans to paragraphs
        textSpans.forEach(span => {
          try {
            // If the span contains a single formatting element, use that instead
            if (span.childNodes.length === 1 && 
                span.firstChild instanceof Element && 
                ['B', 'STRONG', 'I', 'EM', 'U'].includes(span.firstChild.tagName)) {
              if (span.parentNode) {
                span.parentNode.replaceChild(span.firstChild, span);
              }
            } else {
              // Create a new paragraph
              const newP = document.createElement('p');
              
              // Move all children
              while (span.firstChild) {
                newP.appendChild(span.firstChild);
              }
              
              // Replace the span with the paragraph
              if (span.parentNode) {
                span.parentNode.replaceChild(newP, span);
              }
            }
          } catch (err) {
            console.warn('Error converting span to paragraph:', err);
          }
        });
      };
      
      // Apply span formatting fixes
      try {
        fixSpanFormatting();
      } catch (err) {
        console.warn('Error during span formatting fix:', err);
      }
      
      // STEP 6: Fix nested formatting elements
      const fixNestedFormatting = () => {
        // Find formatting elements containing paragraph tags
        const formattingWithP = tempDiv.querySelectorAll('b p, strong p, i p, em p, u p');
        
        formattingWithP.forEach(p => {
          try {
            if (p.parentNode) {
              // Create a document fragment
              const fragment = document.createDocumentFragment();
              
              // Move all children
              while (p.firstChild) {
                fragment.appendChild(p.firstChild);
              }
              
              // Replace the paragraph with its content
              p.parentNode.replaceChild(fragment, p);
            }
          } catch (err) {
            console.warn('Error fixing nested formatting:', err);
          }
        });
      };
      
      // Apply nested formatting fixes
      try {
        fixNestedFormatting();
      } catch (err) {
        console.warn('Error during nested formatting fix:', err);
      }
      
      // STEP 7: Remove unnecessary nested elements
      const removeDuplicateNesting = () => {
        // Find elements with exactly one child of the same type
        const selfNested = Array.from(tempDiv.querySelectorAll('*')).filter(el => {
          return el.childNodes.length === 1 && 
                 el.firstChild instanceof Element && 
                 el.tagName === el.firstChild.tagName;
        });
        
        // Remove the inner duplicate
        selfNested.forEach(el => {
          try {
            if (el.firstChild) {
              const innerEl = el.firstChild as Element;
              
              // Move all children from inner to outer
              while (innerEl.firstChild) {
                el.appendChild(innerEl.firstChild);
              }
              
              // Remove the empty inner element
              el.removeChild(innerEl);
            }
          } catch (err) {
            console.warn('Error removing duplicate nesting:', err);
          }
        });
      };
      
      // Apply duplicate nesting removal
      try {
        removeDuplicateNesting();
      } catch (err) {
        console.warn('Error during duplicate nesting removal:', err);
      }
      
      // STEP 8: CRITICAL - Final pass to fix paragraphs that contain only a single formatting element
      const unwrapSingleFormattingInParagraphs = () => {
        // Find paragraphs that contain ONLY a single formatting element
        const paragraphsWithSingleFormatting = Array.from(tempDiv.querySelectorAll('p')).filter(p => {
          // Check if paragraph contains exactly one child and it's a formatting element
          return p.childNodes.length === 1 && 
                 p.firstChild instanceof Element && 
                 ['B', 'STRONG', 'I', 'EM', 'U'].includes(p.firstChild.tagName) &&
                 p.textContent && p.textContent.trim() !== '';
        });
        
        // Replace these paragraphs with just the formatting element
        paragraphsWithSingleFormatting.forEach(p => {
          try {
            const formattingEl = p.firstChild as Element;
            
            if (p.parentNode) {
              p.parentNode.replaceChild(formattingEl, p);
            }
          } catch (err) {
            console.warn('Error unwrapping formatting from paragraph:', err);
          }
        });
      };
      
      // Apply unwrapping of formatting elements from paragraphs
      try {
        unwrapSingleFormattingInParagraphs();
      } catch (err) {
        console.warn('Error during formatting element unwrapping:', err);
      }
      
      // STEP 9: Remove empty elements
      const removeEmptyElements = () => {
        // Run multiple passes to catch all empty elements
        for (let i = 0; i < 3; i++) {
          // Find all empty elements 
          const emptyElements = tempDiv.querySelectorAll('p:empty, div:empty, span:empty, b:empty, strong:empty, i:empty, em:empty, u:empty');
          
          emptyElements.forEach(el => {
            try {
              if (el.parentNode) {
                el.parentNode.removeChild(el);
              }
            } catch (err) {
              console.warn('Error removing empty element:', err);
            }
          });
          
          // Also find elements with only whitespace
          const allElements = tempDiv.querySelectorAll('p, div, span, b, strong, i, em, u');
          
          Array.from(allElements).forEach(el => {
            try {
              if ((!el.textContent || el.textContent.trim() === '') && !el.querySelector('img, br, hr')) {
                if (el.parentNode) {
                  el.parentNode.removeChild(el);
                }
              }
            } catch (err) {
              console.warn('Error removing whitespace element:', err);
            }
          });
        }
      };
      
      // Apply empty element removal
      try {
        removeEmptyElements();
      } catch (err) {
        console.warn('Error during empty element removal:', err);
      }
      
      // STEP 10: Get the cleaned HTML and perform final string replacements
      let finalHtml = tempDiv.innerHTML;
      
      // Remove any remaining break tags
      finalHtml = finalHtml.replace(/<br[^>]*>/gi, '');
      finalHtml = finalHtml.replace(/<br\s*\/?>/g, '');
      
      // Remove empty elements via string replacement
      finalHtml = finalHtml.replace(/<p>\s*<\/p>/gi, '');
      finalHtml = finalHtml.replace(/<div>\s*<\/div>/gi, '');
      finalHtml = finalHtml.replace(/<span>\s*<\/span>/gi, '');
      finalHtml = finalHtml.replace(/<b>\s*<\/b>/gi, '');
      finalHtml = finalHtml.replace(/<strong>\s*<\/strong>/gi, '');
      finalHtml = finalHtml.replace(/<i>\s*<\/i>/gi, '');
      finalHtml = finalHtml.replace(/<em>\s*<\/em>/gi, '');
      finalHtml = finalHtml.replace(/<u>\s*<\/u>/gi, '');
      
      // Remove empty style attributes
      finalHtml = finalHtml.replace(/\sstyle="\s*"/g, '');
      
      // Remove Tailwind CSS variables
      finalHtml = finalHtml.replace(/\s--tw-[^:;]+(:[^;]+)?;/g, '');
      
      // Remove &nbsp; that are often used for spacing
    //   finalHtml = finalHtml.replace(/&nbsp;/g, ' ');
      
      return finalHtml;
    } catch (error) {
      console.error('Error in cleanHtml:', error);
      
      // At minimum, remove BR tags in case of error
      return html.replace(/<br[^>]*>/gi, '');
    }
  };